import React, { useCallback, useState, useMemo, useEffect } from 'react';
import Shield from '@gravity-ui/icons/Shield';
import i18n from '@yandex-int/i18n';
import cls from 'classnames';
import { Route, Switch, Redirect } from 'react-router';
import { IconSmall } from '../AppIcons/AppIcons';
import { DescribeAppInIcons } from '../DescribeAppInIcons/DescribeAppInIcons';
import { SideMenu } from '../Menu/Menu';
import { MenuMobile, IMenuMobileItem } from '../MenuMobile/MenuMobile';
import { OperationStatus } from '../statuses/OperationStatus/OperationStatus';
import { InstallableAppSettingsPage } from '../InstallableAppSettingsPage/InstallableAppSettingsPage';
import * as keyset from 'i18n/InstallableAppsIndex';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { DeploymentStatus, DeploymentStatusMobile } from '../statuses/DeploymentStatus/DeploymentStatus';
import { useInterval } from 'client/hooks/useInterval';
import { useIsDraft } from 'client/hooks/useIsDraft';
import { isInDevelopment } from 'client/model/draft';
import { InstallableApp } from 'client/model/skill';
import { makeSafe } from 'client/utils';
import { getOperationsTranslated } from 'client/api';
import { Operation } from 'client/model/operation';
import { routes } from 'client/lib/routes';
import { SettingsPageContext } from 'client/context/settingsPageContext';
import { ErrorBoundary } from 'client/blocks/ErrorBoundary/ErrorBoundary';
import { NdaLink } from 'client/blocks-redesign/NdaLink/NdaLink';
import { SettingPageLayout } from '../Layout/Layout';
import { mapChannelToInstallableAppCard } from '../icons/InstallableAppIcons';

import styles from './InstallableAppPage.module.scss';

interface Props {
    installableApp: InstallableApp;
}

const getHeader = (installableApp: InstallableApp) =>
    installableApp.onAir ? installableApp.name : installableApp.draft.name;

export const InstallableAppPage: React.FC<Props> = ({ installableApp }) => {
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const { isDraft } = useIsDraft(installableApp.channel, installableApp.id);

    const t = useMemo(() => i18n(keyset), []);

    const [operations, setOperations] = useState<Operation[]>([]);
    const addOperation = useCallback(
        (operation: Operation) => {
            setOperations([operation, ...operations]);
        },
        [operations],
    );

    const settingRoute = routes.app.installableAppPage(installableApp.channel, isDraft, 'settings', installableApp.id);
    const menuItems: IMenuMobileItem[] = [
        {
            value: `${settingRoute}/main`,
            content: t('main-information'),
            items: [
                {
                    value: `${settingRoute}/main#about`,
                    content: t('about-application'),
                },
                {
                    value: `${settingRoute}/main#application`,
                    content: t('application'),
                },
                {
                    value: `${settingRoute}/main#assets`,
                    content: t('assets'),
                },
                {
                    value: `${settingRoute}/main#agreement`,
                    content: t('agreements'),
                },
            ],
        },
    ];

    if (__appConfig.user.isAdmin) {
        menuItems.push({
            value: routes.app.installableAppSettings(
                installableApp.channel,
                isDraft,
                'feature-flags',
                installableApp.id,
            ),
            content: t('feature-flags'),
        });
    }

    const externalAsideLinks: IMenuMobileItem[] = [
        {
            value: routes.documentation.aliceSkills.feedback,
            content: t('feedback'),
        },
        {
            value: routes.documentation.index,
            content: t('docs'),
        },
    ];

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetchOperations = useCallback(
        makeSafe(async () => {
            const operationsTranslated = await getOperationsTranslated(installableApp.id);
            setOperations(operationsTranslated);
        }),
        [],
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => void fetchOperations(), [installableApp.id]);

    useInterval(fetchOperations, 1 * 60 * 1000);

    return (
        <ErrorBoundary withHomepageLink>
            <SettingsPageContext.Provider
                value={{
                    isEditable: isDraft && isInDevelopment(installableApp),
                    hasUnsavedChanges,
                    setHasUnsavedChanges,
                }}
            >
                <CurrentAliceAppContext.Provider
                    value={{
                        aliceApp: installableApp,
                        operations,
                        addOperation,
                        lastOperationType: operations?.[0]?.type,
                        lastOperationComment: operations?.[0]?.comment,
                        fetchOperations,
                    }}
                >
                    <SettingPageLayout>
                        <section className={styles.container}>
                            <div className={styles.leftColumn}>
                                <div>
                                    <div className={styles.menuHeader}>
                                        <div className={styles.menuHeader__installableAppNameBlock}>
                                            <IconSmall
                                                imageUrl={
                                                    installableApp.onAir
                                                        ? installableApp.logoUrl
                                                        : installableApp.draft?.logoUrl
                                                }
                                            />
                                            <h4 className={styles.menuHeader__header}>{getHeader(installableApp)}</h4>
                                        </div>
                                        <div className={styles.iconStatusBlock}>
                                            {__appConfig.user.isAdmin && <Shield className={styles.adminIcon} />}
                                            {mapChannelToInstallableAppCard?.[installableApp?.channel]?.icon}
                                        </div>
                                    </div>
                                    <DeploymentStatus />
                                    <SideMenu menuItems={menuItems} />
                                </div>
                                <div>
                                    {externalAsideLinks.map((asideLink) => (
                                        <a
                                            key={asideLink.value}
                                            href={asideLink.value}
                                            className={cls(styles.link, styles.footerElement)}
                                        >
                                            {asideLink.content}
                                        </a>
                                    ))}
                                    <div className={styles.footerElement}>
                                        <NdaLink />
                                        {t('yandex-copyright')}
                                    </div>
                                </div>
                            </div>
                            <div className={styles.installableAppPageContent}>
                                <header className={styles.header__container}>
                                    <div className={styles.header__statusContainer}>
                                        <div className={styles.header__commonStatusContainer}>
                                            <DescribeAppInIcons installableApp={installableApp} />
                                            <div className={styles.header__operationStatusDesktop}>
                                                <OperationStatus isDraft={isDraft} />
                                            </div>
                                        </div>
                                        <DeploymentStatusMobile />
                                    </div>
                                </header>
                                <MenuMobile
                                    menuItems={menuItems}
                                    currentPath={`${settingRoute}/main`}
                                    externalAsideLinks={externalAsideLinks}
                                >
                                    <Switch>
                                        <Route
                                            path={routes.app.installableAppPage(
                                                installableApp.channel,
                                                true,
                                                'settings',
                                            )}
                                        >
                                            <InstallableAppSettingsPage />
                                        </Route>
                                        <Route
                                            path={routes.app.installableAppPage(
                                                installableApp.channel,
                                                false,
                                                'settings',
                                            )}
                                        >
                                            <InstallableAppSettingsPage />
                                        </Route>
                                        <Redirect
                                            from={routes.app.installableApp(installableApp.channel)}
                                            to={routes.app.installableAppPage(installableApp.channel, true, 'settings')}
                                        />
                                    </Switch>
                                </MenuMobile>
                                <div className={styles.footerMobile}>
                                    <NdaLink />
                                    {t('yandex-copyright')}
                                </div>
                            </div>
                        </section>
                    </SettingPageLayout>
                </CurrentAliceAppContext.Provider>
            </SettingsPageContext.Provider>
        </ErrorBoundary>
    );
};
